import React from 'react';
import { number, string } from 'prop-types';
import { useCabinetVisualizerStore } from '../../../../store';
import {
  selectDesignProducts,
  getSegments,
  DEFAULT_TOUCH_POINT_WIDTH
} from '../../../../store/cabinetVisualizer/selectors';
import {
  cabinetToBoundingBox,
  getConstrainedWallHeight,
  getTotalBounding,
  segmentsToBoundingBox
} from '../utils';

import { DimensionArrow } from './DimensionArrow';
import { inchesToPxs } from '../../../../utils/wall-size-utils';

export const DimensionArrowsContainer = ({
  designId,
  rows,
  columns,
  pxInInches,
  zoom,
  anchorSide,
  floorOffset
}) => {

  const { cabinetVisualizer } = useCabinetVisualizerStore();

  const products = (designId && cabinetVisualizer) ? selectDesignProducts(cabinetVisualizer, designId) : [];

  const zoomPx = zoom * pxInInches;
  const arrowInchesToPxs = (value) => inchesToPxs(value, zoomPx);

  // All the info the the currently selected cabinet to add
  const segments = segmentsToBoundingBox(getSegments(columns, getConstrainedWallHeight(rows)));
  const cabinetBoundings = products?.map((cab) => cabinetToBoundingBox(cab));
  const totalBounds = getTotalBounding(cabinetBoundings);

  function getCabinetBoundingsBySide() {
    return {
      left: cabinetBoundings.filter((curCab) => curCab.left === totalBounds.left),
      right: cabinetBoundings.filter((curCab) => curCab.right === totalBounds.right),
      top: cabinetBoundings.filter((curCab) => curCab.top <= segments[1].top),
      bottom: cabinetBoundings.filter((curCab) => curCab.bottom === segments[2].bottom)
    };
  }
  const cabinetsBySide = getCabinetBoundingsBySide();

  function calculateArrows() {
    const padding = 3;
    const paddingLeft = padding;
    const paddingRight = padding;
    const size = 2;
    const topYPosition = -padding - floorOffset - totalBounds.height;

    let topPosX = 0;
    let bottomPosX = 0;
    let topLength = 0;
    let bottomLength = 0;

    // Move the top and bottom arrows to the correct x position
    if (anchorSide === 'right') {
      topLength = totalBounds.right - Math.min(...cabinetsBySide.top?.map((cur) => cur?.left));
      bottomLength = totalBounds.right - Math.min(...cabinetsBySide.bottom?.map((cur) => cur?.left));

      topPosX = -topLength - DEFAULT_TOUCH_POINT_WIDTH; // TODO: REMOVE THIS TOUCHPOINT PADDING
      bottomPosX = -bottomLength - DEFAULT_TOUCH_POINT_WIDTH; // TODO: REMOVE THIS TOUCHPOINT PADDING
    } else {
      topPosX = totalBounds.left;
      bottomPosX = totalBounds.left;

      topLength = Math.max(...cabinetsBySide.top?.map((cur) => cur?.right)) - totalBounds.left;
      bottomLength = Math.max(...cabinetsBySide.bottom?.map((cur) => cur?.right)) - totalBounds.left;
    }

    const result = {
      left: {
        posX: DEFAULT_TOUCH_POINT_WIDTH - paddingLeft,
        posY: topYPosition,
        type: 'vertical',
        size,
        length: totalBounds.bottom - totalBounds.top,
        key: 'left'
      },
      right: {
        posX: -DEFAULT_TOUCH_POINT_WIDTH + paddingRight,
        posY: topYPosition,
        type: 'vertical',
        size,
        length: totalBounds.bottom - totalBounds.top,
        key: 'right'
      },
      top: {
        posX: topPosX,
        posY: topYPosition,
        type: 'horizontal',
        size,
        length: topLength,
        key: 'top'
      },
      bottom: {
        posX: bottomPosX,
        posY: padding - floorOffset,
        type: 'horizontal',
        size,
        length: bottomLength,
        key: 'bottom'
      },
    };

    return result;
  }

  let arrowsArray = [];

  if (cabinetBoundings.length > 0) {
    const arrows = calculateArrows();
    arrowsArray = [];
    if (arrows.top.length > 0) {
      arrowsArray.push(arrows.top);
    }

    if (arrows.bottom.length > 0) {
      arrowsArray.push(arrows.bottom);
    }
    arrowsArray.push(arrows[anchorSide]);
  }

  return (
    <div className="dimension-arrows-container">
      {
        arrowsArray.map(({
          posX, posY, size, length, type, key
        }) => (
          <DimensionArrow
            key={key}
            posX={posX}
            posY={posY}
            size={size}
            length={length}
            type={type}
            scaleFn={arrowInchesToPxs}
          />
        ))
      }
    </div>
  );
};

DimensionArrowsContainer.propTypes = {
  designId: string.isRequired,
  rows: number,
  columns: number,
  anchorSide: string,
  pxInInches: number.isRequired,
  zoom: number,
  floorOffset: number,
};

DimensionArrowsContainer.defaultProps = {
  rows: 0,
  columns: 0,
  anchorSide: 'left',
  zoom: 1,
  floorOffset: 0
};
